import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deactivateWhatsappCredentials,
  deleteWhatsappCredentialAccount,
  getWhatsappAccountPhoneNumbers,
  getWhatsappCredentialAccounts,
  getWhatsappCredentialsStatus,
  refreshWhatsappMessagingLimits,
  saveWhatsappCredentialAccounts,
  validateWhatsappCredentialAccount,
} from "../../api/bMarketingApi";

function serializeWhatsappCredentialAccount(account = {}) {
  return {
    ...(account.id ? { id: account.id } : {}),
    account_name: account.accountName ?? account.account_name ?? "",
    app_id: account.appId ?? account.app_id ?? "",
    business_id: account.businessId ?? account.business_id ?? "",
    access_token: account.accessToken ?? account.access_token ?? "",
    auto_reply_enabled: account.autoReplyEnabled ?? account.auto_reply_enabled ?? true,
    auto_reply_message: account.autoReplyMessage ?? account.auto_reply_message ?? "",
  };
}

export function useGetWhatsappCredentialsStatus(organisationId) {
  return useQuery({
    queryKey: ["whatsapp-credentials-status", organisationId],
    queryFn: () => getWhatsappCredentialsStatus(organisationId),
    enabled: !!organisationId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useGetWhatsappCredentialAccounts(organisationId) {
  return useQuery({
    queryKey: ["whatsapp-credential-accounts", organisationId],
    queryFn: () => getWhatsappCredentialAccounts(organisationId),
    enabled: !!organisationId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useGetWhatsappAccountPhoneNumbers(organisationId, accountId, enabled = true) {
  return useQuery({
    queryKey: ["whatsapp-account-phone-numbers", organisationId, accountId],
    queryFn: () => getWhatsappAccountPhoneNumbers(organisationId, accountId),
    enabled: Boolean(organisationId && accountId && enabled),
    staleTime: 1000 * 60 * 5,
  });
}

export function useRefreshWhatsappMessagingLimits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organisation_id }) => refreshWhatsappMessagingLimits(organisation_id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["whatsapp-credential-accounts", variables.organisation_id] });
      queryClient.invalidateQueries({ queryKey: ["emailActivityStats", variables.organisation_id] });
    },
  });
}

export function useValidateWhatsappCredentialAccount() {
  return useMutation({
    mutationKey: ["validate-whatsapp-credential-account"],
    mutationFn: (data) => validateWhatsappCredentialAccount(data),
  });
}

export function useSaveWhatsappCredentialAccounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organisation_id, accounts }) => saveWhatsappCredentialAccounts({
      organisation_id,
      accounts: (accounts || []).map(serializeWhatsappCredentialAccount),
    }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["whatsapp-credentials-status", variables.organisation_id] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-credential-accounts", variables.organisation_id] });
    },
  });
}

export function useDeactivateWhatsappCredentials() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organisation_id }) => deactivateWhatsappCredentials({ organisation_id }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["whatsapp-credentials-status", variables.organisation_id] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-credential-accounts", variables.organisation_id] });
    },
  });
}

export function useDeleteWhatsappCredentialAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organisation_id, account_id }) => deleteWhatsappCredentialAccount({ organisation_id, account_id }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["whatsapp-credentials-status", variables.organisation_id] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-credential-accounts", variables.organisation_id] });
    },
  });
}
